import React from 'react';
import { PageHeader } from "@/components/ui/page-header";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useCredits } from "@/hooks/useCredits";
import { useAuth } from "@/hooks/useAuth";
import { 
  Coins, 
  TrendingUp, 
  TrendingDown, 
  Clock, 
  FileText, 
  Target, 
  Linkedin, 
  CreditCard,
  ArrowUpRight,
  RefreshCw
} from "lucide-react";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";

const actionTypeIcons: Record<string, React.ReactNode> = {
  resume_tailoring: <FileText className="w-4 h-4" />,
  cover_letter_generation: <FileText className="w-4 h-4" />,
  resume_match_analysis: <Target className="w-4 h-4" />,
  linkedin_profile_generation: <Linkedin className="w-4 h-4" />,
  credit_purchase: <CreditCard className="w-4 h-4" />,
  subscription_credits: <Coins className="w-4 h-4" />,
  admin_adjustment: <RefreshCw className="w-4 h-4" />,
};

const actionTypeLabels: Record<string, string> = {
  resume_tailoring: 'Resume Tailoring',
  cover_letter_generation: 'Cover Letter',
  resume_match_analysis: 'Match Score Analysis',
  linkedin_profile_generation: 'LinkedIn Profile',
  credit_purchase: 'Credit Purchase',
  subscription_credits: 'Subscription Credits',
  admin_adjustment: 'Admin Adjustment',
};

export default function CreditHistory() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { balance, plan, transactions, isLoading, refetch, isLowBalance, isCriticalBalance } = useCredits();

  if (!user) {
    return (
      <div className="container mx-auto py-6">
        <Card className="max-w-md mx-auto">
          <CardHeader>
            <CardTitle>Authentication Required</CardTitle>
            <CardDescription>Please log in to view your credit history.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/auth')}>Sign In</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6">
      <PageHeader
        title="Credit History"
        description="View your credit balance and transaction history"
        backTo="/"
      />

      <div className="max-w-4xl mx-auto space-y-6">
        {/* Credit Balance Overview */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card className={`${isCriticalBalance ? 'border-destructive bg-destructive/5' : isLowBalance ? 'border-orange-500 bg-orange-50' : 'border-primary/20 bg-primary/5'}`}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Current Balance</p>
                  <p className="text-4xl font-bold">{balance}</p>
                  <p className="text-xs text-muted-foreground">credits</p>
                </div>
                <div className={`p-3 rounded-full ${isCriticalBalance ? 'bg-destructive/10' : isLowBalance ? 'bg-orange-100' : 'bg-primary/10'}`}>
                  <Coins className={`w-8 h-8 ${isCriticalBalance ? 'text-destructive' : isLowBalance ? 'text-orange-600' : 'text-primary'}`} />
                </div>
              </div>
              {(isLowBalance || isCriticalBalance) && (
                <p className={`text-sm mt-2 ${isCriticalBalance ? 'text-destructive' : 'text-orange-600'}`}>
                  {isCriticalBalance ? 'Critical: Refill soon!' : 'Running low on credits'}
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Current Plan</p>
                  <p className="text-2xl font-bold">{plan?.name || 'Free'}</p>
                  <p className="text-xs text-muted-foreground">
                    {plan?.monthly_credits || 5} credits/month
                  </p>
                </div>
                <div className="p-3 rounded-full bg-accent/10">
                  <CreditCard className="w-8 h-8 text-accent-foreground" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col h-full justify-between">
                <p className="text-sm text-muted-foreground mb-2">Need more credits?</p>
                <Button onClick={() => navigate('/pricing')} className="w-full">
                  <ArrowUpRight className="w-4 h-4 mr-2" />
                  Upgrade Plan
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Transaction History */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Transaction History</CardTitle>
              <CardDescription>Your recent credit transactions</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isLoading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : transactions.length === 0 ? (
              <div className="text-center py-12">
                <Coins className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No transactions yet</h3>
                <p className="text-muted-foreground">
                  Your credit transactions will appear here.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {transactions.map((tx) => (
                  <div key={tx.id} className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-full ${tx.credits_change > 0 ? 'bg-emerald-100' : 'bg-red-100'}`}>
                        {tx.credits_change > 0 ? (
                          <TrendingUp className="w-5 h-5 text-emerald-600" />
                        ) : (
                          <TrendingDown className="w-5 h-5 text-red-600" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          {actionTypeIcons[tx.action_type] || <Coins className="w-4 h-4" />}
                          <p className="font-medium">
                            {actionTypeLabels[tx.action_type] || tx.action_type}
                          </p>
                        </div>
                        {tx.description && (
                          <p className="text-sm text-muted-foreground">{tx.description}</p>
                        )}
                        <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                          <Clock className="w-3 h-3" />
                          {format(new Date(tx.created_at), 'MMM d, yyyy h:mm a')}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge variant={tx.credits_change > 0 ? 'default' : 'destructive'} className="text-lg px-3 py-1">
                        {tx.credits_change > 0 ? '+' : ''}{tx.credits_change}
                      </Badge>
                      <p className="text-xs text-muted-foreground mt-1">
                        Balance: {tx.credits_after}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Credit Cost Reference */}
        <Card>
          <CardHeader>
            <CardTitle>Credit Costs</CardTitle>
            <CardDescription>How many credits each action costs</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                <FileText className="w-5 h-5 text-primary" />
                <div>
                  <p className="font-medium">Resume Tailoring</p>
                  <p className="text-sm text-muted-foreground">3-7 credits (varies)</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                <FileText className="w-5 h-5 text-primary" />
                <div>
                  <p className="font-medium">Cover Letter</p>
                  <p className="text-sm text-muted-foreground">2 credits</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                <Target className="w-5 h-5 text-primary" />
                <div>
                  <p className="font-medium">Match Analysis</p>
                  <p className="text-sm text-muted-foreground">3 credits</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                <Linkedin className="w-5 h-5 text-primary" />
                <div>
                  <p className="font-medium">LinkedIn Profile</p>
                  <p className="text-sm text-muted-foreground">5 credits</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
